// What Quick Entry recognised, taken over into a to-do (QuickParser.swift: Todo.apply, setReminder, ensureDate).
// The parser (core/parser.ts) reads the title; this puts the result on the to-do.

import { at } from "../core/days";
import type { QuickParseResult } from "../core/parser";
import type { Todo } from "../core/records";
import type { Day } from "../core/types";

export interface QuickEntryContext {
  /** The browser's date: a time or a repeat without a day hangs on today. */
  today: Day;
  /** The browser's zone: a reminder at 15:00 is 15:00 here. */
  zone: string;
}

/**
 * The to-do with what was recognised in its title. A title of nothing but recognised words stays as typed. A time
 * becomes a reminder on the to-do's day (today when it has none); a repeat needs a day too; filed into a project or
 * area, an Inbox item goes to Anytime.
 */
export function applyQuickEntry(todo: Todo, result: QuickParseResult, context: QuickEntryContext): Todo {
  if (result.tokens.length === 0 || result.title === "") return todo;
  const next: Todo = { ...todo, title: result.title, tags: [...todo.tags] };

  for (const token of result.tokens) {
    switch (token.kind) {
      case "start":
        next.start = token.value;
        break;
      case "deadline":
        next.deadline = token.value;
        break;
      case "repeats":
        next.repeats = token.value.rule;
        if (token.value.start !== undefined) next.start = { kind: "on", day: token.value.start };
        break;
      case "tag":
        if (!next.tags.includes(token.value)) next.tags.push(token.value);
        break;
      case "project":
        if (next.projectID !== token.value) delete next.sectionID;
        next.projectID = token.value;
        delete next.areaID;
        break;
      case "area":
        delete next.projectID;
        delete next.sectionID;
        next.areaID = token.value;
        break;
      case "time":
      case "habit":
        break;
    }
  }

  // Times and repeats need a day to hang on.
  for (const token of result.tokens) {
    if (token.kind !== "time") continue;
    ensureDate(next, context.today);
    const day = next.start.kind === "on" ? next.start.day : context.today;
    next.reminder = at(day, token.value.hour, token.value.minute, context.zone);
  }
  if (next.repeats !== undefined) ensureDate(next, context.today);
  if (next.start.kind === "inbox" && (next.projectID !== undefined || next.areaID !== undefined)) next.start = { kind: "anytime" };
  return next;
}

/** Gives the to-do a day if it has none yet: today. */
function ensureDate(todo: Todo, today: Day): void {
  if (todo.start.kind !== "on") todo.start = { kind: "on", day: today };
}
